import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  TextInput,
  Stack,
  Text,
  Box,
  Flex,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { EditableExpenditure, ExpenditureCategory } from '../hooks/useExpenditures';
import { showError } from 'src/toasts/toasts';
import React, { useEffect } from 'react';
import { calculateAnnualAmount, calculateMonthlyAmount, formatCurrency } from '../utils';

const frequencyOptions = [
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Annually', label: 'Annually' },
  { value: 'One-off', label: 'One-off' },
];

const categoryOptions: { value: ExpenditureCategory; label: string }[] = [
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Cable', label: 'Cable' },
  { value: 'Online', label: 'Online' },
  { value: 'Service', label: 'Service' },
  { value: 'Other bills', label: 'Other bills' },
  { value: 'Telephone', label: 'Telephone' },
  { value: 'Utilities', label: 'Utilities' },
  { value: 'Office supplies', label: 'Office supplies' },
  { value: 'Advertising', label: 'Advertising' },
  { value: 'Business', label: 'Business' },
  { value: 'Postage', label: 'Postage' },
  { value: 'Printing', label: 'Printing' },
  { value: 'Office maintenance', label: 'Office maintenance' },
  { value: 'Wages', label: 'Wages' },
  { value: 'ATM', label: 'ATM' },
  { value: 'Entertainment', label: 'Entertainment' },
  { value: 'Restaurants', label: 'Restaurants' },
  { value: 'Hobbies', label: 'Hobbies' },
  { value: 'Dues', label: 'Dues' },
  { value: 'Gambling', label: 'Gambling' },
  { value: 'Charitable', label: 'Charitable' },
  { value: 'Gifts', label: 'Gifts' },
  { value: 'Groceries', label: 'Groceries' },
  { value: 'General', label: 'General' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Personal', label: 'Personal' },
  { value: 'Child', label: 'Child' },
  { value: 'Clothing', label: 'Clothing' },
  { value: 'Education', label: 'Education' },
  { value: 'Home Maintenance', label: 'Home maintenance' },
  { value: 'Home Improvement', label: 'Home improvement' },
  { value: 'Pets', label: 'Pets' },
  { value: 'Electronic', label: 'Electronic' },
  { value: 'Mixed', label: 'Mixed' },
  { value: 'Uncategorized', label: 'Uncategorised' },
  { value: 'Other', label: 'Other' },
  { value: 'Cheques', label: 'Cheques' },
  { value: 'Rent', label: 'Rent' },
  { value: 'Loans', label: 'Loans' },
  { value: 'Mortgages', label: 'Mortgages' },
  { value: 'Taxes', label: 'Taxes' },
  { value: 'Automotive', label: 'Automotive' },
  { value: 'Gasoline', label: 'Gasoline' },
  { value: 'Travel', label: 'Travel' },
];

const defaultExpenditure: Omit<EditableExpenditure, 'id' | 'clientId'> = {
  description: '',
  category: categoryOptions[0].value,
  frequency: 'Monthly',
  amount: 0,
};

type ExpenditureModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>) => Promise<void>;
  initialValues?: EditableExpenditure;
};

export function ExpenditureModal({
  opened,
  onClose,
  onSubmit,
  initialValues,
}: ExpenditureModalProps) {
  const form = useForm<Omit<EditableExpenditure, 'id' | 'clientId'>>({
    initialValues: initialValues || defaultExpenditure,
    validate: {
      amount: value =>
        typeof value !== 'number' || value < 0 ? 'Amount must be a number (0 or greater)' : null,
    },
    validateInputOnBlur: true,
  });

  // Reset form when modal opens/closes or initialValues changes
  useEffect(() => {
    if (opened) {
      form.setValues(initialValues || defaultExpenditure);
    }
  }, [opened, initialValues]);

  const handleSubmit = async (values: Omit<EditableExpenditure, 'id' | 'clientId'>) => {
    try {
      await onSubmit(values);
      form.reset();
    } catch {
      showError('Failed to save expenditure');
    }
  };

  const getMonthlyAmount = (amount: number) => {
    return calculateMonthlyAmount(amount, form.values.frequency);
  };

  const getAnnualAmount = (amount: number) => {
    return calculateAnnualAmount(amount, form.values.frequency);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={initialValues?.id ? 'Edit expenditure' : 'Add expenditure'}
    >
      <form onSubmit={form.onSubmit(handleSubmit)} data-testid="expenditure-modal">
        <Stack gap="xs">
          <TextInput label="Description" {...form.getInputProps('description')} required />
          <Select
            label="Category"
            data={categoryOptions}
            {...form.getInputProps('category')}
            required
          />
          <Select
            label="Frequency"
            data={frequencyOptions}
            {...form.getInputProps('frequency')}
            required
          />
          <NumberInput
            label="Amount"
            {...form.getInputProps('amount')}
            required
            leftSection="£"
            hideControls
          />
        </Stack>

        <Box mt="xl">
          <Flex align="center" gap="md" mb="sm">
            <Text fw={500} size="xs" c="gray.4" p={0} m={0}>
              Spend preview
            </Text>
            <Box flex={1} h={1} bg="gray.2" />
          </Flex>
          <Flex gap="md">
            <Stack gap="xxxs">
              <Text fw={500} size="xs" c="gray.10">
                Monthly
              </Text>
              <Text fw={600} size="md" c="gray.10">
                {formatCurrency(getMonthlyAmount(form.values.amount))}
              </Text>
            </Stack>
            <Stack gap="xxxs">
              <Text fw={500} size="xs" c="gray.10">
                Annually
              </Text>
              <Text fw={600} size="md" c="gray.10">
                {formatCurrency(getAnnualAmount(form.values.amount))}
              </Text>
            </Stack>
          </Flex>
        </Box>

        <Group justify="flex-end" mt="xl">
          <Button variant="filled-white" fullWidth type="submit">
            {initialValues ? 'Confirm & save' : 'Confirm & add'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
